/**
 * Находит view (диаграммы), в которых присутствует элемент или связь.
 */
import {getViewLabel} from './getViewLabel';

export type ViewWithConcept = {
	view: {id?: string; name?: string | {value?: string}};
	viewId: string;
	viewName: string;
};

/** Извлечение id из ref (moddle может хранить id в $attrs) */
function getRefId(ref: {id?: string; $attrs?: {id?: string}} | null): string | undefined {
	return ref?.id ?? ref?.$attrs?.id;
}

/**
 * Рекурсивно ищет viewElement по elementRef/relationshipRef.
 */
function searchViewElements(
	elements:
		| Array<{
				elementRef?: {id?: string; $attrs?: {id?: string}};
				relationshipRef?: {id?: string; $attrs?: {id?: string}};
				viewElements?: unknown[];
		  }>
		| undefined,
	conceptId: string,
	isRelationship: boolean
): boolean {
	if (!elements || !Array.isArray(elements)) return false;
	for (const ve of elements) {
		if (isRelationship) {
			if (getRefId(ve.relationshipRef as {id?: string; $attrs?: {id?: string}}) === conceptId)
				return true;
		} else {
			if (getRefId(ve.elementRef as {id?: string; $attrs?: {id?: string}}) === conceptId)
				return true;
		}
		if (
			ve.viewElements &&
			searchViewElements(ve.viewElements as typeof elements, conceptId, isRelationship)
		) {
			return true;
		}
	}
	return false;
}

/**
 * Возвращает id источника и цели связи по id связи.
 * Используется для fallback-навигации, когда связь не отображается (например, composition).
 * @returns { sourceId?: string; targetId?: string } или null если связь не найдена
 */
export function getRelationshipEndpointIds(
	model: {
		relationshipsNode?: {
			relationship?: Array<{
				id?: string;
				source?: {id?: string; $attrs?: {id?: string}};
				target?: {id?: string; $attrs?: {id?: string}};
			}>;
		};
	} | null,
	relationshipId: string
): {sourceId?: string; targetId?: string} | null {
	const rels = model?.relationshipsNode?.relationship ?? [];
	const relsArr = Array.isArray(rels) ? rels : rels ? [rels] : [];
	const rel = relsArr.find(r => getRefId(r as {id?: string}) === relationshipId) ?? null;
	if (!rel) return null;
	return {
		sourceId: getRefId(rel.source as {id?: string; $attrs?: {id?: string}}),
		targetId: getRefId(rel.target as {id?: string; $attrs?: {id?: string}}),
	};
}

/**
 * Возвращает список view, содержащих элемент (conceptId) или связь.
 * @param model — moddle-модель ArchiMate
 * @param conceptId — id элемента (из elementsNode) или связи (из relationshipsNode)
 * @param isRelationship — true если conceptId — это id связи
 */
type ViewLike = {
	id?: string;
	name?: string | {value?: string};
	viewElements?: unknown[];
	$attrs?: {id?: string};
};

export function findViewsContainingConcept(
	model: {views?: {diagrams?: {viewsList?: ViewLike[]}}} | null,
	conceptId: string,
	isRelationship: boolean
): ViewWithConcept[] {
	const viewsList = model?.views?.diagrams?.viewsList ?? [];
	const result: ViewWithConcept[] = [];
	for (const view of viewsList) {
		const found = searchViewElements(
			view.viewElements as Array<{
				elementRef?: {id?: string};
				relationshipRef?: {id?: string};
				viewElements?: unknown[];
			}>,
			conceptId,
			isRelationship
		);
		if (found) {
			const viewId = view.id ?? view.$attrs?.id ?? '';
			result.push({view, viewId, viewName: getViewLabel(view)});
		}
	}
	return result;
}
